import * as React from 'react';
import * as CollapsiblePrimitive from '@radix-ui/react-collapsible';
import { cn } from '@/lib/utils';

const Collapsible = CollapsiblePrimitive.Root;

const CollapsibleTrigger = CollapsiblePrimitive.CollapsibleTrigger;

const CollapsibleContent = React.forwardRef(
  ({ className, children, ...props }, ref) => (
    <CollapsiblePrimitive.CollapsibleContent
      ref={ref}
      className={cn(
        'overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down',
        className,
      )}
      {...props}
    >
      {children}
    </CollapsiblePrimitive.CollapsibleContent>
  ),
);
CollapsibleContent.displayName = 'CollapsibleContent';

export { Collapsible, CollapsibleTrigger, CollapsibleContent };

// Legacy Collapse component that mimics MUI Collapse behavior
// Keep this for backwards compatibility with files using `in` prop
export const Collapse = React.forwardRef(
  (
    {
      in: isOpen,
      timeout = 300,
      unmountOnExit = false,
      children,
      className,
      ...props
    },
    ref,
  ) => {
    const [shouldRender, setShouldRender] = React.useState(isOpen);
    const [height, setHeight] = React.useState(isOpen ? 'auto' : 0);
    const contentRef = React.useRef(null);

    React.useEffect(() => {
      if (isOpen) {
        setShouldRender(true);
        requestAnimationFrame(() => {
          if (contentRef.current) {
            const scrollHeight = contentRef.current.scrollHeight;
            setHeight(scrollHeight);
            setTimeout(
              () => {
                if (isOpen) setHeight('auto');
              },
              typeof timeout === 'number' ? timeout : 300,
            );
          }
        });
      } else {
        if (contentRef.current) {
          const scrollHeight = contentRef.current.scrollHeight;
          setHeight(scrollHeight);
          requestAnimationFrame(() => {
            setHeight(0);
          });
        }
        if (unmountOnExit) {
          setTimeout(
            () => {
              setShouldRender(false);
            },
            typeof timeout === 'number' ? timeout : 300,
          );
        }
      }
    }, [isOpen, timeout, unmountOnExit]);

    if (!shouldRender && unmountOnExit) {
      return null;
    }

    const duration = timeout === 'auto' ? 300 : timeout;

    return (
      <div
        ref={ref}
        className={cn('overflow-hidden', className)}
        style={{
          height: height,
          transition: `height ${duration}ms cubic-bezier(0.4, 0, 0.2, 1)`,
        }}
        {...props}
      >
        <div ref={contentRef}>{children}</div>
      </div>
    );
  },
);

Collapse.displayName = 'Collapse';
